import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { space, SpaceProps } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { Text, Flex, Title, Col, Row, Box, Link } from '@firsttable/web-components/atoms';
import spaces from '@firsttable/web-theme/styledSpaces';
import { City } from '@firsttable/types';
import IconArrowLeftNav from '@firsttable/web-components/atoms/Icon/IconArrowLeftNav';
import IconArrowRightNav from '@firsttable/web-components/atoms/Icon/IconArrowRightNav';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import RestaurantOtherItem from '../RestaurantOtherItem';

const Arrow = styled(Box)`
  outline: 0;
  color: ${themeGet('colors.brand.blue')};

  &:active {
    color: ${themeGet('colors.gold.900')};
  }
`;

export const Slider = styled.div<SpaceProps>`
  display: flex;
  overflow: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  &::after {
    content: '';
    display: block;
    width: 1px;
    flex-shrink: 0;
  }
  ${space};
`;

const SliderItem = styled(Box)`
  flex-shrink: 0;
`;

type RestaurantOtherProps = {
  restaurants: any;
  availabilitySession: string;
  region: any;
  city?: City;
  title?: string;
  NavComponent: any;
};
const RestaurantOther = ({
  restaurants,
  availabilitySession,
  region,
  city,
  title,
  NavComponent,
  ...props
}: RestaurantOtherProps & SpaceProps) => {
  const slider = useRef(null);
  const spaceBetween = 20;
  const restaurantWidth = 285;
  const { track } = useAnalytics();
  const go = (dir) => {
    track('button_clicked', {
      button_name: 'scroll carousel',
      button_type: 'scroll',
      button_copy: `${dir} icon`,
    });
    const el = slider.current;
    const offset = restaurantWidth + spaceBetween;

    el.scrollTo({
      left: el.scrollLeft + (dir === 'back' ? -offset : offset),
      behavior: 'smooth',
    });
  };

  if (!region) {
    return null;
  }
  let currentRestaurants = restaurants.edges.map(({ node }) => node);

  let displayCity = false;
  // need to check if city is available. As this comp gets loaded on home as well.

  if (city) {
    const cityRestaurants = restaurants.edges
      .filter(({ node }) => node.cityId === city.id)
      .map(({ node }) => node);

    displayCity = cityRestaurants.length > 2;
    currentRestaurants = displayCity ? cityRestaurants : currentRestaurants;
  }

  const slug = displayCity ? city.slug : region.slug;
  const cityTitle = displayCity ? city.menuTitle : region.menuTitle;
  const sliderTitle = title || `Nearby ${cityTitle} restaurants`;

  return (
    !!currentRestaurants.length && (
      <Box {...spaces(props)} data-nosnippet>
        <Row>
          <Col>
            <Box
              textAlign={['center', null, 'left']}
              display={[null, null, 'flex']}
              alignItems="center"
              justifyContent="space-between"
              mb={36}>
              <TrackProvider link_name="nearby city restaurants title">
                <Link NavComponent={NavComponent} to={slug} color="brand.blue">
                  <Title
                    display="inline"
                    css="&:hover {border-bottom: 2px solid;}"
                    mb={['l', null, 0]}
                    textAlign="center"
                    color={null}>
                    {sliderTitle}
                  </Title>
                </Link>
              </TrackProvider>
              <Flex mt={[25, 35, 15]} justifyContent="center">
                <Arrow onClick={() => go('back')} role="button" aria-label="previous">
                  <IconArrowLeftNav color={null} />
                </Arrow>
                <Arrow ml="m" onClick={() => go('forward')} role="button" aria-label="next">
                  <IconArrowRightNav color={null} />
                </Arrow>
              </Flex>
            </Box>
          </Col>
        </Row>
        <Slider ref={slider}>
          {currentRestaurants.slice(0, 20).map((node) => (
            <SliderItem key={node.id} width={restaurantWidth} mr={spaceBetween} mb={60}>
              <TrackProvider component_name={`${node.title} restaurant card`}>
                <RestaurantOtherItem
                  NavComponent={NavComponent}
                  restaurant={node}
                  width={restaurantWidth}
                  availabilitySession={
                    node.sessionTypes.includes(availabilitySession)
                      ? availabilitySession
                      : node.sessionTypes[0]
                  }
                  allowFiltering={false}
                  selectedDate="any"
                />
              </TrackProvider>
            </SliderItem>
          ))}
          {currentRestaurants.length > 20 && (
            <SliderItem colors="card.s" width={restaurantWidth} mr={spaceBetween} mb={60}>
              <TrackProvider link_name="nearby city restaurants title">
                <Link
                  NavComponent={NavComponent}
                  href={city?.slug || region.slug}
                  height="100%"
                  display="flex">
                  <Box m="auto" textAlign="center" maxWidth="70%">
                    <Text
                      forwardedAs="span"
                      color="brand.blue"
                      css="border-bottom: 1px solid #26568245;"
                      m={0}>
                      More {displayCity ? city.menuTitle : region.menuTitle} restaurants
                    </Text>
                  </Box>
                </Link>
              </TrackProvider>
            </SliderItem>
          )}
        </Slider>
      </Box>
    )
  );
};

RestaurantOther.propTypes = {
  restaurants: PropTypes.any,
  region: PropTypes.any,
  city: PropTypes.any,
  title: PropTypes.string,
  availabilitySession: PropTypes.string.isRequired,
  NavComponent: PropTypes.any.isRequired,
};

export default RestaurantOther;
